/**
 * AssigneeDisplay Component
 *
 * Displays the currently assigned team member with avatar and name.
 * Shows "Unassigned" if no one is assigned.
 */

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useProjectMembers } from '@/hooks/useProjectMembers'
import { Skeleton } from '@/components/ui/skeleton'

interface AssigneeDisplayProps {
  projectId: string
  userId: string | null | undefined
  showUnassigned?: boolean
  className?: string
}

export function AssigneeDisplay({
  projectId,
  userId,
  showUnassigned = true,
  className = '',
}: AssigneeDisplayProps) {
  const { data: members, isLoading } = useProjectMembers(projectId)

  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Skeleton className="h-6 w-6 rounded-full" />
        <Skeleton className="h-4 w-24" />
      </div>
    )
  }

  if (!userId) {
    if (!showUnassigned) return null

    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center">
          <span className="text-xs text-muted-foreground">-</span>
        </div>
        <span className="text-sm text-muted-foreground">Unassigned</span>
      </div>
    )
  }

  const member = members?.find(m => m.user_id === userId)

  if (!member) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Avatar className="h-6 w-6">
          <AvatarFallback className="text-xs">?</AvatarFallback>
        </Avatar>
        <span className="text-sm text-muted-foreground">Unknown User</span>
      </div>
    )
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Avatar className="h-6 w-6">
        <AvatarImage src={member.profile?.avatar_url} />
        <AvatarFallback className="text-xs">
          {member.profile?.full_name?.charAt(0).toUpperCase() ||
           member.profile?.email?.charAt(0).toUpperCase() || '?'}
        </AvatarFallback>
      </Avatar>
      <span className="text-sm font-medium">
        {member.profile?.full_name || member.profile?.email || 'Unknown'}
      </span>
    </div>
  )
}
